import { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { ROLE_HOME, type Permission, type SystemRole } from '@aurea/contracts';
import { lazyPage } from './lib/lazy-page';
import { AppLayout } from './components/AppLayout';
import { RequireAuth } from './components/RequireAuth';
import { useAuth } from './lib/auth';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { RecoverPage } from './pages/RecoverPage';

/*
 * Страницы кабинетов грузятся по требованию. Вход и регистрация остаются
 * в основном бандле: это первое, что открывает человек, и ждать вторую
 * загрузку на них незачем. Кабинеты же нужны только своей роли — соискателю
 * никогда не понадобится код админки.
 */
const InvitesPage = lazyPage(() =>
  import('./pages/InvitesPage').then((m) => ({ default: m.InvitesPage })),
);
const PasswordPage = lazyPage(() =>
  import('./pages/PasswordPage').then((m) => ({ default: m.PasswordPage })),
);
const LearnPage = lazyPage(() =>
  import('./pages/candidate/LearnPage').then((m) => ({ default: m.LearnPage })),
);
const CompanyPage = lazyPage(() =>
  import('./pages/candidate/CompanyPage').then((m) => ({ default: m.CompanyPage })),
);
const SettingsPage = lazyPage(() =>
  import('./pages/candidate/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const TodayPage = lazyPage(() =>
  import('./pages/recruiter/TodayPage').then((m) => ({ default: m.TodayPage })),
);
const MeetingsPage = lazyPage(() =>
  import('./pages/recruiter/MeetingsPage').then((m) => ({ default: m.MeetingsPage })),
);
const RecruiterAnalyticsPage = lazyPage(() =>
  import('./pages/recruiter/AnalyticsPage').then((m) => ({ default: m.RecruiterAnalyticsPage })),
);
const TemplatesPage = lazyPage(() =>
  import('./pages/recruiter/TemplatesPage').then((m) => ({ default: m.TemplatesPage })),
);
const CandidatesPage = lazyPage(() =>
  import('./pages/training/CandidatesPage').then((m) => ({ default: m.CandidatesPage })),
);
const KanbanPage = lazyPage(() =>
  import('./pages/training/KanbanPage').then((m) => ({ default: m.KanbanPage })),
);
const InternshipsPage = lazyPage(() =>
  import('./pages/training/InternshipsPage').then((m) => ({ default: m.InternshipsPage })),
);
const DurationsPage = lazyPage(() =>
  import('./pages/stats/DurationsPage').then((m) => ({ default: m.DurationsPage })),
);
const TrainingAnalyticsPage = lazyPage(() =>
  import('./pages/training/TrainingAnalyticsPage').then((m) => ({
    default: m.TrainingAnalyticsPage,
  })),
);
const EmployeesPage = lazyPage(() =>
  import('./pages/head/EmployeesPage').then((m) => ({ default: m.EmployeesPage })),
);
const HeadKanbanPage = lazyPage(() =>
  import('./pages/head/HeadKanbanPage').then((m) => ({ default: m.HeadKanbanPage })),
);
const LeftPage = lazyPage(() =>
  import('./pages/head/LeftPage').then((m) => ({ default: m.LeftPage })),
);
const HeadStatsPage = lazyPage(() =>
  import('./pages/head/HeadStatsPage').then((m) => ({ default: m.HeadStatsPage })),
);
const StatusLogPage = lazyPage(() =>
  import('./pages/head/StatusLogPage').then((m) => ({ default: m.StatusLogPage })),
);
const FunnelPage = lazyPage(() =>
  import('./pages/stats/FunnelPage').then((m) => ({ default: m.FunnelPage })),
);
const UsersPage = lazyPage(() =>
  import('./pages/admin/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const RolesPage = lazyPage(() =>
  import('./pages/admin/RolesPage').then((m) => ({ default: m.RolesPage })),
);
const CourseEditorPage = lazyPage(() =>
  import('./pages/admin/CourseEditorPage').then((m) => ({ default: m.CourseEditorPage })),
);
const AdminChangesPage = lazyPage(() =>
  import('./pages/admin/ChangesPage').then((m) => ({ default: m.ChangesPage })),
);
const CompanyEditorPage = lazyPage(() =>
  import('./pages/admin/CompanyEditorPage').then((m) => ({ default: m.CompanyEditorPage })),
);
const AdminSettingsPage = lazyPage(() =>
  import('./pages/admin/SettingsPage').then((m) => ({ default: m.AdminSettingsPage })),
);

export function App() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/r/:token" element={<RegisterPage />} />
        <Route path="/recover" element={<RecoverPage />} />

        {/* ─── Соискатель ─────────────────────────────────────────── */}
        <Route
          path="/me"
          element={
            <RequireAuth>
              <AppLayout
                nav={[
                  { to: '/me', label: 'Обучение' },
                  { to: '/me/company', label: 'О компании' },
                  { to: '/me/settings', label: 'Настройки' },
                ]}
              />
            </RequireAuth>
          }
        >
          {/*
            Обучение стало главной страницей кабинета: вкладки «Статус»
            и «Контакты» убраны. Прежние адреса ведут на обучение — ссылки
            из старых писем и закладок не должны упираться в пустоту.
          */}
          <Route index element={<LearnPage />} />
          <Route path="learn" element={<Navigate to="/me" replace />} />
          <Route path="company" element={<CompanyPage />} />
          <Route path="contacts" element={<Navigate to="/me" replace />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* ─── Рекрутёр ───────────────────────────────────────────── */}
        <Route
          path="/recruiter"
          element={
            <RequireAuth permission="meetings.read">
              <AppLayout
                nav={[
                  { to: '/recruiter', label: 'Сегодня' },
                  { to: '/recruiter/meetings', label: 'Встречи' },
                  { to: '/recruiter/analytics', label: 'Аналитика', permission: 'analytics.read' },
                  { to: '/recruiter/templates', label: 'Шаблоны и скрипты' },
                  { to: '/recruiter/invites', label: 'Ссылки', permission: 'invites.create' },
                  { to: '/recruiter/password', label: 'Пароль' },
                ]}
              />
            </RequireAuth>
          }
        >
          <Route index element={<TodayPage />} />
          <Route path="meetings" element={<MeetingsPage />} />
          <Route path="analytics" element={<RecruiterAnalyticsPage />} />
          <Route path="templates" element={<TemplatesPage />} />
          <Route path="invites" element={<InvitesPage />} />
          <Route path="password" element={<PasswordPage />} />
        </Route>

        {/* ─── Руководитель обучения ──────────────────────────────── */}
        <Route
          path="/training"
          element={
            <RequireAuth permission="candidates.read">
              <AppLayout
                nav={[
                  { to: '/training', label: 'Список' },
                  { to: '/training/kanban', label: 'По статусам' },
                  { to: '/training/internships', label: 'Выходы' },
                  { to: '/training/analytics', label: 'Аналитика', permission: 'analytics.read' },
                  { to: '/training/password', label: 'Пароль' },
                ]}
              />
            </RequireAuth>
          }
        >
          <Route index element={<CandidatesPage />} />
          <Route path="kanban" element={<KanbanPage />} />
          <Route path="internships" element={<InternshipsPage />} />
          <Route path="analytics" element={<TrainingAnalyticsPage />} />
          <Route path="password" element={<PasswordPage />} />
        </Route>

        {/* ─── Руководитель ───────────────────────────────────────── */}
        <Route
          path="/head"
          element={
            <RequireAuth permission="analytics.full">
              <AppLayout
                nav={[
                  { to: '/head', label: 'Сотрудники' },
                  { to: '/head/kanban', label: 'По статусам' },
                  { to: '/head/left', label: 'Ушедшие' },
                  { to: '/head/stats', label: 'Статистика' },
                  { to: '/head/log', label: 'Изменения', permission: 'analytics.full' },
                  { to: '/head/password', label: 'Пароль' },
                ]}
              />
            </RequireAuth>
          }
        >
          <Route index element={<EmployeesPage />} />
          <Route path="kanban" element={<HeadKanbanPage />} />
          <Route path="left" element={<LeftPage />} />
          <Route path="stats" element={<HeadStatsPage />} />
          <Route path="log" element={<StatusLogPage />} />
          <Route path="password" element={<PasswordPage />} />
        </Route>

        {/* ─── Статистика ─────────────────────────────────────────── */}
        <Route
          path="/stats"
          element={
            <RequireAuth permission="analytics.read">
              <AppLayout
                nav={[
                  { to: '/stats', label: 'Воронка' },
                  { to: '/stats/durations', label: 'Сроки' },
                  { to: '/stats/password', label: 'Пароль' },
                ]}
              />
            </RequireAuth>
          }
        >
          <Route index element={<FunnelPage />} />
          <Route path="durations" element={<DurationsPage />} />
          <Route path="password" element={<PasswordPage />} />
        </Route>

        {/* ─── Администратор ──────────────────────────────────────── */}
        {/*
          Раздел открывается любым из своих прав, а каждая вкладка — своим.
          Раньше дверь запиралась правом на управление пользователями: выдача
          права на курс не давала ничего, потому что до вкладки было не дойти.
        */}
        <Route
          path="/admin"
          element={
            <RequireAuth permission={ADMIN_PERMISSIONS}>
              <AppLayout
                nav={[
                  { to: '/admin', label: 'Пользователи', permission: 'users.manage' },
                  { to: '/admin/roles', label: 'Роли и доступы', permission: 'roles.manage' },
                  { to: '/admin/course', label: 'Курс', permission: 'course.edit' },
                  { to: '/admin/company', label: 'О компании', permission: 'course.edit' },
                  { to: '/admin/changes', label: 'История изменений', permission: 'audit.read' },
                  { to: '/admin/invites', label: 'Ссылки', permission: 'invites.create' },
                  { to: '/admin/settings', label: 'Настройки', permission: 'settings.manage' },
                  { to: '/admin/password', label: 'Пароль' },
                ]}
              />
            </RequireAuth>
          }
        >
          <Route index element={<AdminHome />} />
          <Route
            path="roles"
            element={
              <RequireAuth permission="roles.manage">
                <RolesPage />
              </RequireAuth>
            }
          />
          <Route
            path="course"
            element={
              <RequireAuth permission="course.edit">
                <CourseEditorPage />
              </RequireAuth>
            }
          />
          <Route
            path="company"
            element={
              <RequireAuth permission="course.edit">
                <CompanyEditorPage />
              </RequireAuth>
            }
          />
          <Route
            path="changes"
            element={
              <RequireAuth permission="audit.read">
                <AdminChangesPage />
              </RequireAuth>
            }
          />
          <Route
            path="invites"
            element={
              <RequireAuth permission="invites.create">
                <InvitesPage />
              </RequireAuth>
            }
          />
          <Route
            path="settings"
            element={
              <RequireAuth permission="settings.manage">
                <AdminSettingsPage />
              </RequireAuth>
            }
          />
          <Route path="password" element={<PasswordPage />} />
        </Route>

        <Route path="/" element={<HomeRedirect />} />
        <Route path="*" element={<HomeRedirect />} />
      </Routes>
    </Suspense>
  );
}

function PageLoading() {
  return (
    <div className="flex min-h-full items-center justify-center text-[13px] text-ink-faint">
      Загружаем…
    </div>
  );
}

/** Корень уводит в кабинет своей роли, а гостя — на страницу входа. */
/** Права, любое из которых открывает раздел администрирования. */
const ADMIN_PERMISSIONS = [
  'users.manage',
  'roles.manage',
  'course.edit',
  'settings.manage',
  'audit.read',
] as const satisfies readonly Permission[];

/**
 * Первая страница раздела.
 *
 * Список пользователей — не для всех, кого сюда пускают: рекрутёру могли
 * выдать только курс. Поэтому без права на пользователей уводим на первую
 * доступную вкладку, а не показываем отказ на входе.
 */
function AdminHome() {
  const { can } = useAuth();

  if (can('users.manage')) return <UsersPage />;

  const first = [
    ['course.edit', '/admin/course'],
    ['roles.manage', '/admin/roles'],
    ['audit.read', '/admin/changes'],
    ['invites.create', '/admin/invites'],
    ['settings.manage', '/admin/settings'],
  ] as const satisfies readonly (readonly [Permission, string])[];

  const target = first.find(([permission]) => can(permission))?.[1] ?? '/admin/password';

  return <Navigate to={target} replace />;
}

function HomeRedirect() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center text-[13px] text-ink-faint">
        Загружаем…
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  return <Navigate to={ROLE_HOME[user.role.code as SystemRole] ?? '/me'} replace />;
}
